import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@skupreme/shared/ui';
import { FormItem } from '@skupreme/shared/ui/form';
import { PlusIcon } from '@phosphor-icons/react';
import { CurrencyMaskInput, NumberMaskInput } from '@elements/CustomMaskInput';
import { ToastMessage } from '@utility/toast';
import { ErrorMessageConstant } from '@utility/constants/common';
import { LoaderBar } from '@components/LoaderBar';
import PurchaseOrdersService from '@utility/services/purchase-order';
import ItemService from '@utility/services/item';
import { BoldText } from '@elements/Common';
import { AppModal, AppModalXIcon } from '@elements/Modal';

interface IPrice {
  moq: number;
  price: number;
}

interface IItemForPricing {
  item_upc: string;
  prices?: IPrice[];
  supplier_id?: string;
  lead_time?: number;
}

interface IEditItemPricesPopupProps {
  open: boolean;
  closeModal: (prices?: IPrice[]) => void;
  poId?: string;
  item?: IItemForPricing;
  currency?: string;
}

/* The masks hold strings; the schema turns them into the numbers the API takes. */
const tierAmount = (label: string) =>
  z.coerce
    .number({ invalid_type_error: `${label} is required` })
    .positive(`${label} must be greater than 0`);

const itemPriceSchema = z.object({
  prices: z.array(
    z.object({
      moq: tierAmount('Order Quantity'),
      price: tierAmount('Cost Per Item'),
    }),
  ),
});

/* What the masked inputs hold until the schema has coerced it. */
type ItemPriceForm = {
  prices: { moq: string | number | null; price: string | number | null }[];
};

export function EditItemPricesPopup({
  open,
  closeModal,
  poId,
  item,
  currency,
}: IEditItemPricesPopupProps) {
  const [saving, setSaving] = useState<string>('');
  const {
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm<ItemPriceForm>({
    resolver: zodResolver(itemPriceSchema),
    defaultValues: { prices: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'prices',
    keyName: 'id',
  });

  useEffect(() => {
    if (open && item?.item_upc) {
      const itemPrices = [...(item.prices || [])].sort((a, b) => a.moq - b.moq);

      reset({
        prices: itemPrices.map(price => ({
          moq: price.moq || 1,
          price: price.price,
        })),
      });
    } else {
      reset({ prices: [] });
    }
  }, [open, item, reset]);

  const addNew = () => {
    /* A first tier starts at one unit; later ones are left for the user to fill. */
    append({ moq: fields.length ? null : 1, price: 0 });
  };

  const onSubmit = async (formData: ItemPriceForm) => {
    if (!item) return;

    const tiers: IPrice[] = formData.prices.map(tier => ({
      moq: Number(tier.moq),
      price: Number(tier.price),
    }));

    try {
      setSaving('Saving');
      let max = tiers[0].moq;
      for (let index = 1; index < tiers.length; index += 1) {
        const element = tiers[index];
        if (element.moq <= max) {
          ToastMessage.error('Order quantity should be in ascending order');
          return;
        }
        max = element.moq;
      }

      const valueArr = tiers.map(x => x.price);
      const isDuplicate = valueArr.some(
        (priceItem, idx) => valueArr.indexOf(priceItem) !== idx,
      );

      if (isDuplicate) {
        ToastMessage.error('Cost should be unique');
        return;
      }

      if (poId) {
        const bodyData = {
          items: [
            {
              item_upc: item.item_upc,
              prices: [...tiers].sort((a, b) => a.moq - b.moq),
            },
          ],
        };
        await PurchaseOrdersService.updateItemPrices(poId, bodyData);
      } else {
        const bodyData = {
          items: [
            {
              item_upc: item.item_upc,
              supplier_id: item.supplier_id,
              lead_time: item.lead_time || 0,
              prices: [...tiers].sort((a, b) => a.moq - b.moq),
            },
          ],
        };
        await ItemService.update(bodyData);
      }

      ToastMessage.success('Item Costs has been updated successfully.');
      closeModal(tiers);
    } catch (error: unknown) {
      const err = error as { error?: string };

      ToastMessage.error(err.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving('');
    }
  };

  return (
    <AppModal
      open={open}
      size="small"
      title="Add Tiered Costs"
      closeModal={() => closeModal()}
    >
      {saving && <LoaderBar content={saving} />}
      <form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="sk:mt-4 sk:flex sk:flex-col sk:gap-3"
      >
        {fields.map((fieldItem, index) => (
          <div
            key={fieldItem.id}
            className="sk:flex sk:flex-col sk:gap-2 sk:border-0 sk:border-t sk:border-solid sk:border-rule sk:pt-3 sk:first:border-t-0 sk:first:pt-0"
          >
            <BoldText>Tier {index + 1}</BoldText>
            <div className="sk:grid sk:grid-cols-[1fr_1fr_auto] sk:items-start sk:gap-3">
              <Controller
                control={control}
                name={`prices.${index}.moq`}
                render={({ field }) => (
                  <FormItem
                    label="Order Quantity"
                    isRequired
                    error={errors.prices?.[index]?.moq?.message}
                  >
                    {({ labelId }) => (
                      <NumberMaskInput
                        aria-labelledby={labelId}
                        aria-invalid={!!errors.prices?.[index]?.moq}
                        placeholder="Order Quantity"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  </FormItem>
                )}
              />
              <Controller
                control={control}
                name={`prices.${index}.price`}
                render={({ field }) => (
                  <FormItem
                    label="Cost Per Item"
                    isRequired
                    error={errors.prices?.[index]?.price?.message}
                  >
                    {({ labelId }) => (
                      <CurrencyMaskInput
                        aria-labelledby={labelId}
                        aria-invalid={!!errors.prices?.[index]?.price}
                        placeholder="Cost Per Item"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        currencyCode={currency}
                      />
                    )}
                  </FormItem>
                )}
              />
              <div className="sk:mt-5 sk:size-7">
                {index > 0 && (
                  <AppModalXIcon
                    size={16}
                    label={`Remove tier ${index + 1}`}
                    onClick={() => remove(index)}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
        <div className="sk:mt-2 sk:flex sk:flex-wrap sk:items-center sk:justify-between sk:gap-2">
          <Button onPress={addNew}>
            <PlusIcon aria-hidden />
            Tier
          </Button>
          <div className="sk:flex sk:gap-2">
            <Button onPress={() => closeModal()}>Cancel</Button>
            <Button type="submit" variant="primary" isDisabled={!fields.length}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </AppModal>
  );
}
